import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from 'components/atoms/icons/chevron-right';
import { timesDataSource } from 'infrastructure/data-sources/times';
import { replaceStringByObjectKeys } from 'infrastructure/functions';

import s from './styles.module.scss';

import type { Nullable } from 'infrastructure/types';
import type { EditorLog } from 'infrastructure/classes/logs/editor-log';

type MappedValue = { [key: string]: { [key: string]: string } };

interface IProps {
  data: EditorLog;
}

const ChangesCol: React.FC<IProps> = ({ data }) => {
  const { t } = useTranslation();

  const times = useMemo(() => timesDataSource(), []);

  const checkValue = (value: Nullable<string>) =>
    !value || value === 'null' ? t('labels.bannerNoData') : value;

  const sectionMapper: MappedValue = {
    PatientCareAgents: {
      ccmManager: t('historyLog.ccmCareManager'),
      onboardingManager: t('historyLog.onboardingManager'),
      patManager: t('historyLog.patManager'),
      ccmPatManager: t('historyLog.ccmPatManager'),
      ccmOnboardingAgent: t('historyLog.ccmOnboardingAgent'),
    },
    ContactAvailabilities: {
      null: t('historyLog.anytime'),
      ...Object.fromEntries(times.map(({ id, name }) => [id, name])),
    },
  } as const;

  const getMappedValue = (section: string, value: string) => {
    if (section === 'ContactAvailabilities')
      return replaceStringByObjectKeys(
        value,
        sectionMapper.ContactAvailabilities,
      );

    return sectionMapper[section]?.[value] ?? checkValue(value);
  };

  return (
    <>
      {data.changes.map(({ field, from, to }, i) => (
        <div key={i} className={s.change}>
          <span className={s.date}>{field}</span>
          <div className={s.info}>
            <span className={s.from}>
              {t('labels.from')}: {getMappedValue(data.section, from)}
            </span>
            <ChevronRightIcon />
            <span className={s.to}>
              {t('labels.to')}: {getMappedValue(data.section, to)}
            </span>
          </div>
        </div>
      ))}
    </>
  );
};

export default ChangesCol;
